import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MagnifyingGlass, X, User } from '@phosphor-icons/react';
import { userService, type AccountUser } from '../../services/widgetService';

interface UserSearchInputProps {
  /** User UUIDs to exclude from search results (e.g., already shared users) */
  excludeUserUuids?: string[];
  /** Current user ID to exclude from results */
  currentUserId?: number;
  /** Callback when a user is selected */
  onSelect: (user: AccountUser) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
}

export const UserSearchInput: React.FC<UserSearchInputProps> = ({
  excludeUserUuids = [],
  currentUserId,
  onSelect,
  placeholder = 'Search users by name or email...',
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<AccountUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load users on mount
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const accountUsers = await userService.getAccountUsers();
        setUsers(accountUsers);
      } catch (err) {
        console.error('Failed to load users:', err);
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Filter users based on search term and exclusions
  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return users.filter((user) => {
      // Exclude current user
      if (currentUserId && user.id === currentUserId) return false;

      // Exclude already shared users (by user_uuid)
      if (excludeUserUuids.includes(user.user_uuid)) return false;

      // Filter by search term
      if (!term) return true;

      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email.toLowerCase();

      return fullName.includes(term) || email.includes(term);
    });
  }, [users, searchTerm, excludeUserUuids, currentUserId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (user: AccountUser) => {
    onSelect(user);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    setSearchTerm('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <MagnifyingGlass
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled || loading}
          className={`
            w-full pl-10 pr-10 py-2.5
            border border-gray-300 rounded-lg
            text-sm text-gray-900
            placeholder:text-gray-400
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-50 disabled:cursor-not-allowed
            ${error ? 'border-red-300' : ''}
          `}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {searchTerm ? 'No users found' : 'Start typing to search'}
            </div>
          ) : (
            <ul className="py-1">
              {filteredUsers.slice(0, 10).map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(user)}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user.email}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
              {filteredUsers.length > 10 && (
                <li className="px-4 py-2 text-xs text-gray-400 text-center">
                  +{filteredUsers.length - 10} more users. Refine your search.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearchInput;
